import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import {
	faCheckCircle,
	faSparkles,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { LeadScoringConflict } from "@signalist/shared-types";

import { Icon, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Spinner } from "@/components/Spinner/Spinner";

import { ConflictCheckOutcome } from "./useCriteriaDraft";

const FIXED_CRITERIA = [
	"HQ_COUNTRY",
	"SUBSIDIARY_COUNTRY",
	"INDUSTRY",
	"EMPLOYEE_SIZE",
	"PERSON_LOCATION",
	"JOB_LEVEL",
];

export type ConflictCheckState =
	| { phase: "checking" }
	| { phase: "none" }
	| { phase: "found"; outcome: ConflictCheckOutcome }
	| { phase: "failed" };

interface ConflictCheckModalProps {
	state: ConflictCheckState;
	/** Name of the scoring a conflict belongs to, for the list. */
	scoringName: (scoringId: number) => string;
	onFix: (scoringId: number, conflict: LeadScoringConflict) => void;
	onSaveAnyway: () => void;
	onClose: () => void;
}

/**
 * The "Analyzing your changes" dialog (PREC-4055) that Save opens when a
 * location or industry grade changed: the spinner while the check runs, the
 * green all-clear once the save went through, or the list of values graded
 * in two buckets with a jump to the criterion and a "save anyway".
 */
export const ConflictCheckModal: FunctionComponent<ConflictCheckModalProps> = ({
	state,
	scoringName,
	onFix,
	onSaveAnyway,
	onClose,
}) => {
	const { t } = useTranslation();

	const criterionLabel = (conflict: LeadScoringConflict) =>
		FIXED_CRITERIA.includes(conflict.criterion)
			? t(`leadScoring.scoringPage.criteria.${conflict.criterion}`)
			: conflict.criterion;

	const tierLabel = (points: number) => t(`leadScoring.scoringPage.tiers.${points}`);

	const title = () => {
		switch (state.phase) {
			case "none":
				return t("leadScoring.scoringPage.conflict.noneTitle");
			case "found":
				return t("leadScoring.scoringPage.conflict.foundTitle", {
					count: state.outcome.scorings.reduce((sum, row) => sum + row.conflicts.length, 0),
				});
			default:
				return t("leadScoring.scoringPage.conflict.checkingTitle");
		}
	};

	const footer = () => {
		switch (state.phase) {
			case "checking":
				return <div />;
			case "found":
				return (
					<div className="flex justify-end gap-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							testId="conflict-fix"
							title={t("leadScoring.scoringPage.conflict.fix")}
							type="button"
							onClick={onClose}
						/>
						<Button
							color={ButtonColor.ACTION}
							size={ButtonSize.XS}
							testId="conflict-save-anyway"
							title={t("leadScoring.scoringPage.conflict.saveAnyway")}
							type="button"
							onClick={onSaveAnyway}
						/>
					</div>
				);
			default:
				return (
					<div className="flex justify-end">
						<Button
							color={ButtonColor.ACTION}
							size={ButtonSize.XS}
							testId="conflict-done"
							title={t("leadScoring.scoringPage.conflict.done")}
							type="button"
							onClick={onClose}
						/>
					</div>
				);
		}
	};

	return (
		<Modal
			isOpened
			footer={footer()}
			handleClose={state.phase === "checking" ? undefined : onClose}
			icon={
				<Icon
					className="mr-1 h-8 w-8 rounded-full bg-blue-action-200 p-1.5 text-brand-light"
					icon={faSparkles}
				/>
			}
			size="md"
			title={title()}
		>
			<div className="flex flex-col gap-3 text-sm" data-testid={`conflict-${state.phase}`}>
				{state.phase === "checking" && (
					<>
						<p className="text-text-meta">{t("leadScoring.scoringPage.conflict.checkingBody")}</p>
						<div className="flex items-center gap-2 text-text-meta">
							<Spinner />
							{t("leadScoring.scoringPage.conflict.running")}
						</div>
					</>
				)}
				{state.phase === "none" && (
					<div className="flex items-start gap-2 rounded-lg border border-status-success-light bg-status-success-light px-3 py-2 text-status-success">
						<Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" icon={faCheckCircle} />
						{t("leadScoring.scoringPage.conflict.noneBody")}
					</div>
				)}
				{state.phase === "failed" && (
					<div className="flex items-start gap-2 rounded-lg border border-status-error-light bg-error-light px-3 py-2 text-status-error">
						<Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" icon={faTriangleExclamation} />
						{t("leadScoring.scoringPage.conflict.failed")}
					</div>
				)}
				{state.phase === "found" && (
					<>
						<p className="text-text-meta">{t("leadScoring.scoringPage.conflict.foundBody")}</p>
						{state.outcome.status === "partial" && (
							<p className="text-xs text-tag-warning-dark">
								{t("leadScoring.scoringPage.conflict.partial")}
							</p>
						)}
						<ul className="flex flex-col gap-2">
							{state.outcome.scorings.flatMap((row) =>
								row.conflicts.map((conflict) => (
									<li
										key={`${row.scoringId}-${conflict.section}-${conflict.criterion}-${conflict.values.join("|")}`}
										className="rounded-lg border border-status-error-light bg-error-light px-3 py-2"
									>
										<div className="flex items-center gap-2">
											<span className="text-xs font-semibold text-status-error">
												{scoringName(row.scoringId)} · {criterionLabel(conflict)}
											</span>
											<button
												className="ml-auto cursor-pointer text-[11px] font-semibold text-brand-light hover:underline"
												type="button"
												onClick={() => onFix(row.scoringId, conflict)}
											>
												{t("leadScoring.scoringPage.conflict.fix")}
											</button>
										</div>
										<div className="mt-1 text-xs text-text">
											<b className="font-semibold">{conflict.values.join(", ")}</b>
											{": "}
											{conflict.entries
												.map((entry) =>
													t("leadScoring.scoringPage.conflict.entry", {
														entry: entry.entry,
														tier: tierLabel(entry.points),
													})
												)
												.join(" · ")}
										</div>
										{conflict.reason && (
											<div className="mt-1 text-[11px] text-text-meta">{conflict.reason}</div>
										)}
									</li>
								))
							)}
						</ul>
					</>
				)}
			</div>
		</Modal>
	);
};
